// Copyright 2025-2026 - Hewlett Packard Enterprise Company
import {
  useCallback,
  useEffect,
  useState,
  useMemo,
  createContext,
} from 'react';

import useFlags from '../hooks/useFlags';

import { DARK_MODE_KEY } from '../constants/constants';

export const ThemeModeContext = createContext();

const THEME_MODE_PREFERENCES = {
  AUTO: 'auto',
  DARK: 'dark',
  LIGHT: 'light',
};
const VALID_PREFERENCES = Object.values(THEME_MODE_PREFERENCES);

const getModeString = isDarkMode => (isDarkMode ? 'dark' : 'light');
const isValidPreference = preference => VALID_PREFERENCES.includes(preference);

const getStoredPreference = () => {
  try {
    const storedPreference = localStorage.getItem(DARK_MODE_KEY);
    return isValidPreference(storedPreference)
      ? storedPreference
      : THEME_MODE_PREFERENCES.AUTO;
  } catch (_error) {
    return THEME_MODE_PREFERENCES.AUTO;
  }
};

const getSystemDarkMode = () => {
  if (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
  ) {
    return false;
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const resolveIsDarkMode = ({
  isDarkModeEnabled,
  themeModePreference,
  isDarkModeSystemPreference,
}) => {
  if (!isDarkModeEnabled) {
    return false;
  }

  if (themeModePreference === THEME_MODE_PREFERENCES.DARK) {
    return true;
  }

  if (themeModePreference === THEME_MODE_PREFERENCES.LIGHT) {
    return false;
  }

  return isDarkModeSystemPreference;
};

export default function ThemeModeProvider({ children }) {
  const flags = useFlags();
  const isDarkModeEnabled = flags.T_20250602_70791_ENABLE_UI_DARK_MODE_BOOL();
  const [themeModePreference, setThemeModePreferenceState] =
    useState(getStoredPreference);
  const [isDarkModeSystemPreference, setIsDarkModeSystemPreference] =
    useState(getSystemDarkMode);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return undefined;
    }

    const darkModeMediaQuery = window.matchMedia(
      '(prefers-color-scheme: dark)',
    );

    const handleSystemThemeChange = event => {
      setIsDarkModeSystemPreference(event.matches);
    };

    setIsDarkModeSystemPreference(darkModeMediaQuery.matches);

    if (typeof darkModeMediaQuery.addEventListener === 'function') {
      darkModeMediaQuery.addEventListener('change', handleSystemThemeChange);

      return () => {
        darkModeMediaQuery.removeEventListener(
          'change',
          handleSystemThemeChange,
        );
      };
    }

    darkModeMediaQuery.addListener(handleSystemThemeChange);

    return () => {
      darkModeMediaQuery.removeListener(handleSystemThemeChange);
    };
  }, []);

  useEffect(() => {
    const handleStoredPreferenceChange = event => {
      if (event.key !== DARK_MODE_KEY) {
        return;
      }

      setThemeModePreferenceState(getStoredPreference());
    };

    window.addEventListener('storage', handleStoredPreferenceChange);

    return () => {
      window.removeEventListener('storage', handleStoredPreferenceChange);
    };
  }, []);

  // when cleaning up T_20250602_70791_ENABLE_UI_DARK_MODE_BOOL, remove this effect
  // because the theme provider is a parent of the auth provider and LD flags targeting
  // specific users evaluate after auth, this is a workaround to ensure the storage is
  // read again to initialize the preference state
  useEffect(() => {
    if (isDarkModeEnabled) {
      setThemeModePreferenceState(getStoredPreference());
    }
  }, [isDarkModeEnabled]);

  const setThemeModePreference = useCallback(nextThemeModePreference => {
    if (!isValidPreference(nextThemeModePreference)) {
      return;
    }

    try {
      localStorage.setItem(DARK_MODE_KEY, nextThemeModePreference);
    } catch (_error) {
      // ignore localStorage write errors and still update in-memory state
    }

    setThemeModePreferenceState(nextThemeModePreference);
  }, []);

  const isDarkMode = resolveIsDarkMode({
    isDarkModeEnabled,
    themeModePreference,
    isDarkModeSystemPreference,
  });

  const value = useMemo(
    () => ({
      isDarkMode,
      themeMode: getModeString(isDarkMode),
      themeModePreference,
      setThemeModePreference,
    }),
    [isDarkMode, themeModePreference, setThemeModePreference],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
}
